import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listOrgNetworkConnections,
  getNetworkConnectionsFilterOptions,
} from '@kirin/api-client/api/shadow-ai';
import type {
  NetworkConnectionListItem,
  NetworkConnectionSortField,
  NetworkConnectionsFilterOptionField,
} from '@kirin/api-client/contracts/shadow-ai';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { apiClient } from '@/lib/api-client';
import type { NetworkStatusFilterValue } from './NetworkStatusFilterSelector';

/**
 * On the Unreviewed scope with a hits-desc sort, break hit-count ties by
 * confidence (likely-AI first, then score desc, then lastSeen desc) — the
 * triage order the pre-DataTable table applied. `hitCount` stays the primary
 * key, so the page keeps the server's hits-desc order (KIR-4586). Returns a
 * sorted copy of the current page.
 */
function tieBreakPendingByConfidence(
  connections: NetworkConnectionListItem[]
): NetworkConnectionListItem[] {
  return [...connections].sort((a, b) => {
    if (b.hitCount !== a.hitCount) return b.hitCount - a.hitCount;
    const labelA = a.confidence?.label ?? 'unlikely_ai';
    const labelB = b.confidence?.label ?? 'unlikely_ai';
    if (labelA !== labelB) return labelA.localeCompare(labelB);
    const scoreA = a.confidence?.score ?? 0;
    const scoreB = b.confidence?.score ?? 0;
    if (scoreB !== scoreA) return scoreB - scoreA;
    return new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime();
  });
}

/**
 * KIR-3496: adapt the Network Discovery connections endpoint (which returns the
 * legacy `{ connections, pagination:{page,limit,total,totalPages} }` shape) to
 * the DataTable `DataFetchResponse`. Per-column `filters`, `sort`, and
 * `knownTotal` are forwarded to the server; `status` is the top-level scope
 * driven by the status selector (not a per-column filter).
 */
export async function fetchNetworkDomains(
  organizationId: string,
  params: DataFetchParams,
  signal: AbortSignal | undefined,
  status: NetworkStatusFilterValue
): Promise<DataFetchResponse<NetworkConnectionListItem>> {
  const sortField = params.sort?.field as
    | NetworkConnectionSortField
    | undefined;

  const response = await listOrgNetworkConnections(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      status,
      filters: params.filters,
      // KIR-5098 (P1): main search — host or endpoint name / ID.
      search: params.search,
      sortBy: sortField,
      sortOrder: params.sort?.order,
      knownTotal: params.knownTotal,
      // knownTotal is only present on a sort/page-only change (filters + status
      // unchanged), so tell the backend it can skip the expensive COUNT on
      // `domain_enriched`.
      ...(params.knownTotal != null ? { countScope: 'sort' as const } : {}),
    },
    { signal }
  );

  const isDefaultHitsSort =
    sortField === 'hitCount' && params.sort?.order === SortOrder.Desc;
  const connections =
    status === 'pending' && isDefaultHitsSort
      ? tieBreakPendingByConfidence(response.connections)
      : response.connections;

  const { page, limit, total, totalPages } = response.pagination;

  return {
    data: connections,
    pagination: { page, limit, total, hasMore: page < totalPages },
    // The list endpoint does not echo active filters; the DataTable derives its
    // active-filter chips from client state, so an empty list here is correct.
    meta: { activeFilters: [] },
  };
}

export async function fetchNetworkConnectionsFilterOptions(
  organizationId: string,
  field: NetworkConnectionsFilterOptionField,
  status: NetworkStatusFilterValue,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getNetworkConnectionsFilterOptions(
    apiClient,
    organizationId,
    {
      field,
      status,
      search: params.search,
      limit: params.limit,
      offset: params.offset,
    }
  );

  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}
